<template>
  <!-- Rendered in the vendor row's actions slot, like the Scheduling page. -->
  <input id="po-tracker-search" v-model="text" type="search" class="input !w-52 !py-1.5" placeholder="Search PO…" aria-label="Search PO" @input="onInput" />
  <!-- Read-only: transit time is set on the vendor's settings page. -->
  <span v-if="planner && transitDays !== null" class="text-sm text-gray-600">
    Transit
    <b class="text-gray-900">{{ daysLabel(transitDays) }}</b>
  </span>
  <button v-if="planner" type="button" class="btn-primary" @click="emit('request-delivery')">Request delivery date</button>
  <button v-if="partner" type="button" class="btn-secondary" @click="emit('import-sheet')">Import sheet</button>
  <button v-if="partner" type="button" class="btn-primary" @click="emit('mark-shipped')">Mark shipped</button>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, ref, watch } from 'vue';

  import { daysLabel } from '@/utils/poTracker';
  import { normalisePo } from '@/utils/poTrackerQuery';

  const SEARCH_DEBOUNCE_MS = 300;

  const props = defineProps<{
    search: string;
    planner: boolean;
    partner: boolean;
    transitDays: number | null;
  }>();
  const emit = defineEmits<{
    search: [po: string];
    'request-delivery': [];
    'mark-shipped': [];
    'import-sheet': [];
  }>();

  const text = ref(props.search);
  let timer: ReturnType<typeof setTimeout> | undefined;

  // The URL holds the normalised number; don't overwrite what is being typed when it only
  // differs by case or spaces, but do follow a Clear, Show all POs, a vendor switch or a shared
  // link, and drop a pending keystroke so it can't bring the old search back.
  watch(
    () => props.search,
    (search) => {
      if (search === normalisePo(text.value)) return;
      clearTimeout(timer);
      text.value = search;
    }
  );

  function onInput(): void {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const po = normalisePo(text.value);
      if (po !== props.search) emit('search', po);
    }, SEARCH_DEBOUNCE_MS);
  }

  onBeforeUnmount(() => clearTimeout(timer));
</script>
